import * as React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import glamorous from 'glamorous';
import { RvLoader } from '../../components/Loader';
import { GetGroupByPlurals, GetGroupBy, formatNumber, getSuggestedMaxTick, getMaxTickLimit, getLabels } from '../../helpers/ChartHelper';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, FooterDescription, HelpWrapper } from '../Common';
import { WithStyles, withStyles, IconButton, FormControl, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { styles } from '../styles';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';
import MTooltip from '@material-ui/core/Tooltip';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import { VideoModal } from '../../components/VideoModal';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartStatTiles } from '../ChartStatTiles';
import { ChartLimitNote } from '../ChartLimitNote';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    },
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {
        display: 'flex',
        alignItems: 'center',
    },
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
    [`& .${`h3`}`]: {
        paddingTop: '10px',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    },
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5,
}));

const RadioGroupWrapper = glamorous(RadioGroup)<{ theme?: object }>((props) => ({
    flexFlow: 'row wrap !important',
    justifyContent: 'center',
}));

const AverageFeeEstimatesPeriod = {
    LastSixMonths: 'LastSixMonths',
    LastTwelveMonths: 'LastTwelveMonths',
    LastTwoYears: 'LastTwoYears',
    AllTime: 'AllTime',
} as const;

type AverageFeeEstimatesPeriodOption = typeof AverageFeeEstimatesPeriod[keyof typeof AverageFeeEstimatesPeriod];

interface ChartProps extends SelectedItemProps, ChartCommonProps, WithStyles<typeof styles> {
    groupBy: string;
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ChartState extends ChartCommonState {
    option: AverageFeeEstimatesPeriodOption;
    isVideoModalOpen: boolean;
}

class AverageFeeEstimatesCurrentChart extends React.Component<ChartProps, ChartState> {
    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.handleTotalClick = this.handleTotalClick.bind(this);
        this.onHover = this.onHover.bind(this);
        this.onChangeRadio = this.onChangeRadio.bind(this);
        this.onToggleVideo = this.onToggleVideo.bind(this);

        this.onPan = this.onPan.bind(this);

        this.chartRef = React.createRef();

        this.state = {
            open: false,
            anchorEl: undefined,
            option: 'LastTwelveMonths',
            isVideoModalOpen: false
        };
    }

    public componentDidMount() {
        //
    }

    public componentWillUnmount() {
        //
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
            return true;
        }

        return false;
    }

    public render() {
        // const { classes } = this.props;
        return (
            <React.Fragment>
                {this.state.isVideoModalOpen && this.props.infoUrl && (
                    <VideoModal 
                        open={this.state.isVideoModalOpen}
                        onClose={this.onToggleVideo}
                        url={this.props.infoUrl}
                    />
                )}
                {/* tslint:disable-next-line: no-any */}
                <Query<AverageFeeEstimatesData>
                    query={AverageFeeEstimatesDataQuery}
                    variables={{
                        groupBy: this.props.groupBy,
                        entityType: this.props.chartFilter?.entityType,
                        entityGuid: this.props.chartFilter?.entityGuid,
                        responsibility: this.props.chartFilter?.responsibility,
                    }}
                >
                    {({ loading, error, data }) => {
                        if (loading || error || data === null || data === undefined) {
                            return (
                                <ChartSkeleton variant="bars" />
                            );
                        }

                        const statistics = data.statistics;

                        const matter = loading || error || statistics === null || statistics === undefined ? null : statistics.matter;

                        const currencySymbol = loading || error ? null : data.options.currencySymbol;
                        
                        const symbol = currencySymbol ? currencySymbol.symbol : null;

                        const averageFeeEstimates = matter ? matter.averageFeeEstimates : null;

                        if (averageFeeEstimates) {
                            
                            let averageEstimate: AverageFeeEstimateElement[] = [];
                            let groupedByCount: number = 0;
                            let avgFeeEstimatePerMatter: number = 0;
                            let mattersCount: number = 0;
                            let mattersWithNoEstimate: number = 0;
                            let mattersWithNoEstimateBilledFees: number = 0;
                            let formattedPeriodString: string = ''

                            if (this.state.option === AverageFeeEstimatesPeriod.LastSixMonths && averageFeeEstimates.averageFeeEstimateLastSixMonths) {
                                
                                averageEstimate = averageFeeEstimates.averageFeeEstimateLastSixMonths.averageFeeEstimate;
                                groupedByCount = averageFeeEstimates.averageFeeEstimateLastSixMonths.groupedByCount;
                                
                                avgFeeEstimatePerMatter = averageFeeEstimates.feeEstimateDetailsLastSixMonths.avgFeeEstimatePerMatter;
                                mattersCount = averageFeeEstimates.feeEstimateDetailsLastSixMonths.mattersCount;
                                mattersWithNoEstimate = averageFeeEstimates.feeEstimateDetailsLastSixMonths.mattersWithNoEstimate;
                                mattersWithNoEstimateBilledFees = averageFeeEstimates.feeEstimateDetailsLastSixMonths.mattersWithNoEstimateBilledFees;
                                formattedPeriodString ="Last 6 Months";
                            } 
                            
                            if (this.state.option === AverageFeeEstimatesPeriod.LastTwelveMonths && averageFeeEstimates.averageFeeEstimateLastTwelveMonths) {
                                
                                averageEstimate = averageFeeEstimates.averageFeeEstimateLastTwelveMonths.averageFeeEstimate;
                                groupedByCount = averageFeeEstimates.averageFeeEstimateLastTwelveMonths.groupedByCount;
                                
                                avgFeeEstimatePerMatter = averageFeeEstimates.feeEstimateDetailsLastTwelveMonths.avgFeeEstimatePerMatter;
                                mattersCount = averageFeeEstimates.feeEstimateDetailsLastTwelveMonths.mattersCount;
                                mattersWithNoEstimate = averageFeeEstimates.feeEstimateDetailsLastTwelveMonths.mattersWithNoEstimate;
                                mattersWithNoEstimateBilledFees = averageFeeEstimates.feeEstimateDetailsLastTwelveMonths.mattersWithNoEstimateBilledFees;
                                formattedPeriodString ="Last 12 Months";
                            } 
                            
                            if (this.state.option === AverageFeeEstimatesPeriod.LastTwoYears && averageFeeEstimates.averageFeeEstimateLastTwoYears) {
                            
                                averageEstimate = averageFeeEstimates.averageFeeEstimateLastTwoYears.averageFeeEstimate;
                                groupedByCount = averageFeeEstimates.averageFeeEstimateLastTwoYears.groupedByCount;

                                avgFeeEstimatePerMatter = averageFeeEstimates.feeEstimateDetailsLastTwoYears.avgFeeEstimatePerMatter;
                                mattersCount = averageFeeEstimates.feeEstimateDetailsLastTwoYears.mattersCount;
                                mattersWithNoEstimate = averageFeeEstimates.feeEstimateDetailsLastTwoYears.mattersWithNoEstimate;
                                mattersWithNoEstimateBilledFees = averageFeeEstimates.feeEstimateDetailsLastTwoYears.mattersWithNoEstimateBilledFees;
                                formattedPeriodString ="Last 2 Years";
                            } 
                            if (this.state.option === AverageFeeEstimatesPeriod.AllTime && averageFeeEstimates.averageFeeEstimateAlltime) {
                            
                                averageEstimate = averageFeeEstimates.averageFeeEstimateAlltime.averageFeeEstimate;
                                groupedByCount = averageFeeEstimates.averageFeeEstimateAlltime.groupedByCount;

                                avgFeeEstimatePerMatter = averageFeeEstimates.feeEstimateDetailsAll.avgFeeEstimatePerMatter;
                                mattersCount = averageFeeEstimates.feeEstimateDetailsAll.mattersCount;
                                mattersWithNoEstimate = averageFeeEstimates.feeEstimateDetailsAll.mattersWithNoEstimate;
                                mattersWithNoEstimateBilledFees = averageFeeEstimates.feeEstimateDetailsAll.mattersWithNoEstimateBilledFees;
                                formattedPeriodString ="All Time";
                            }

                            let highestValue = 0;

                            // tslint:disable-next-line:no-any
                            let groupedBy = averageEstimate.map((value: AverageFeeEstimateElement) => {
                                return value.groupedBy;
                            });

                            // tslint:disable-next-line: no-any
                            let amounts = averageEstimate.map((value: AverageFeeEstimateElement) => {
                                if (value.amount > highestValue) {
                                    highestValue = value.amount;
                                }
                                return value.amount;
                            });

                            if (isNaN(avgFeeEstimatePerMatter)) {
                                avgFeeEstimatePerMatter = 0;
                            }

                            return (
                                <BarWrapper className={RV_CHART_CLASS}>
                                    <IconButtonWrapper aria-label="close">
                                        <HelpWrapper className="rv-chart-help">
                                            <ChartInfo description={this.props.description} />
                                            {this.props.infoUrl && (
                                                <MTooltip title="Learn more">
                                                    <IconButton
                                                        style={{ color: 'rgb(0, 120, 212)' }}
                                                        aria-label="maximize"
                                                        onClick={this.onToggleVideo}
                                                        size={this.props.showAllRecords ? 'medium' : 'small'}
                                                    >
                                                        <VideoLibraryOutlinedIcon />
                                                    </IconButton>
                                                </MTooltip>
                                            )}
                                        </HelpWrapper>
                                        <IconButton aria-label="close" onClick={this.onCloseChart} size="small">
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                    <BarTitleContainer>
                                        <ChartHeading icon={<AssessmentOutlinedIcon />} title="Average Fee Estimate per Matter" />
                                        <TotalDiv className="h2" onClick={() => this.handleTotalClick(formattedPeriodString)}>
                                            <AnimatedNumber value={avgFeeEstimatePerMatter} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                                        </TotalDiv>
                                        <div className="h3">
                                            <TotalDiv onClick={() => this.handleTotalClick(formattedPeriodString)}>{formatNumber(mattersCount)}</TotalDiv>
                                            Current Matters with Estimates
                                        </div>
                                        <div className="h3 rv-chart-section">Average Fee Estimate By {GetGroupBy(this.props.groupBy)}</div>
                                    </BarTitleContainer>
                                    <BarContainer className="rv-chart-bars">
                                        <Bar
                                            ref={this.chartRef}
                                            // tslint:disable-next-line: no-any
                                            onClick={(event: React.MouseEvent<any>) => {
                                                const _data = getElementAtEvent(this.chartRef.current, event);
                                                if (_data.length > 0 && this.props.getSelectedItem) {
                                                    const _index = getElementAtEvent(this.chartRef.current, event)[0].index;

                                                    const item = groupedBy[_index];

                                                    const searchCriterias = new Array<SearchCriteria>();

                                                    searchCriterias.push(
                                                        { property: this.props.groupBy, operator: 'eq', comparison: item },
                                                        { property: 'InstructionDate', operator: 'gt', comparison: this.state.option },
                                                    );

                                                    this.props.getSelectedItem(
                                                        searchCriterias,
                                                        false,
                                                        `Average Fee Estimate per Matter By ${GetGroupBy(this.props.groupBy)} - ${item}`,
                                                        DrillDownType.matterSummaryDrilldown,
                                                        'Matters',
                                                    );
                                                }
                                            }}
                                            data={{
                                                labels: getLabels(groupedBy),
                                                datasets: [
                                                    {
                                                        label: 'Fees',
                                                        ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                                        data: amounts,
                                                    },
                                                ],
                                            }}
                                            options={{
                                                onHover: this.onHover,
                                                indexAxis: 'y' as const,
                                                animation: rvChartAnimation(),
                                                elements: { bar: { borderWidth: 0 } },
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                scales: {
                                                    x: {
                                                        grid: rvValueAxisStyle.grid,
                                                        // beginAtZero: true,
                                                        suggestedMax: getSuggestedMaxTick(amounts),
                                                        beginAtZero: true,
                                                        ticks: {
                                                            ...rvValueAxisStyle.ticks,
                                                            maxTicksLimit: getMaxTickLimit(amounts),
                                                            autoSkip: false,
                                                            callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                return `${symbol}${formatNumber(tickValue)}`;
                                                            },
                                                        },
                                                    },
                                                    y: {
                                                        grid: rvCategoryAxisStyle.grid,
                                                        // min: 0,
                                                        // max: 9 // minimum display
                                                        ticks: {
                                                            ...rvCategoryAxisStyle.ticks,
                                                            autoSkip: false,
                                                        },
                                                    },
                                                },
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom' as const,
                                                        display: false,
                                                    },
                                                    title: {
                                                        display: false,
                                                        text: `Estimate vs Billed Fees`,
                                                    },
                                                    zoom: {
                                                        pan: {
                                                            enabled: false,
                                                            onPan: this.onPan,
                                                        },
                                                        zoom: {
                                                            wheel: {
                                                                enabled: false,
                                                            },
                                                        },
                                                        // limits: {
                                                        //   y: { min: 0, max: 9 },
                                                        // },
                                                    },
                                                    tooltip: {
                                                        ...rvTooltipStyle,
                                                        callbacks: {
                                                            // tslint:disable-next-line: no-any
                                                            label: function (context: any) {
                                                                const {
                                                                    dataIndex,
                                                                    //   label,
                                                                    //   formattedValue,
                                                                    //   dataset
                                                                } = context;

                                                                var currentValue = amounts[dataIndex];

                                                                return `${symbol}${formatNumber(currentValue)}`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {isChartDataEmpty(amounts) && (
                                            <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                        )}
                                    </BarContainer>
                                    <FormControl component="div">
                                        <RadioGroupWrapper name="option" value={this.state.option} onChange={this.onChangeRadio}>
                                            <FormControlLabel 
                                                value={AverageFeeEstimatesPeriod.LastSixMonths} 
                                                control={<Radio color="primary" />} 
                                                label="Last 6 Months" 
                                            />
                                            <FormControlLabel 
                                                value={AverageFeeEstimatesPeriod.LastTwelveMonths} 
                                                control={<Radio color="primary" />} 
                                                label="Last 12 Months" 
                                            />
                                            <FormControlLabel 
                                                value={AverageFeeEstimatesPeriod.LastTwoYears} 
                                                control={<Radio color="primary" />} 
                                                label="Last 2 Years" 
                                            />
                                            <FormControlLabel 
                                                value={AverageFeeEstimatesPeriod.AllTime} 
                                                control={<Radio color="primary" />} 
                                                label="All Time" 
                                            />
                                        </RadioGroupWrapper>
                                    </FormControl>
                                    <FooterDescription>
                                        <ChartStatTiles
                                            caption="Matters with no estimate"
                                            tiles={[
                                                { label: 'Matters', value: formatNumber(mattersWithNoEstimate), color: RV_CHART_COLORS.amber, onClick: () => this.handleNoEstimates() },
                                                { label: 'Billed fees', value: `${symbol}${formatNumber(mattersWithNoEstimateBilledFees)}`, color: RV_CHART_COLORS.green },
                                            ]}
                                        />
                                        <ChartNotes notes={['Fees only, disbursements are not included']} />
                                    </FooterDescription>
                                    {groupedByCount > 10 && <ChartLimitNote>The top 10 {GetGroupByPlurals(this.props.groupBy)} are shown</ChartLimitNote>}
                                </BarWrapper>
                            );
                        }

                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick(period: string) {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (this.state.option === AverageFeeEstimatesPeriod.AllTime) {
                searchCriterias.push(
                    { property: 'InProgress', operator: 'eq', comparison: '1' },
                    { property: 'EstimatedCosts', operator: 'gt', comparison: '0' }
                );
            } else {
                searchCriterias.push(
                    { property: 'InProgress', operator: 'eq', comparison: '1' },
                    { property: 'InstructionDate', operator: 'gt', comparison: this.state.option },
                    { property: 'EstimatedCosts', operator: 'gt', comparison: '0' },
                    { property: 'AverageEstimatedCosts', operator: '', comparison: ''}
                );
            }
            const title = `Average Fee Estimate per Matter by ${GetGroupBy(this.props.groupBy)} - ${period}`;

            this.props.getSelectedItem(searchCriterias, false, title, DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private handleNoEstimates() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (this.state.option === AverageFeeEstimatesPeriod.AllTime) {
                searchCriterias.push(
                    { property: 'InProgress', operator: 'eq', comparison: '1' },
                    { property: 'EstimatedCosts', operator: 'lteq', comparison: '0' }
                );
            } else {
                searchCriterias.push(
                    { property: 'InProgress', operator: 'eq', comparison: '1' },
                    { property: 'InstructionDate', operator: 'gt', comparison: this.state.option },
                    { property: 'EstimatedCosts', operator: 'lteq', comparison: '0' },
                    { property: 'AverageEstimatedCosts', operator: '', comparison: ''}
                );
            }
            const title = `Average Fee Estimate for ${this.state.option} by ${GetGroupBy(this.props.groupBy)}`;

            this.props.getSelectedItem(searchCriterias, false, title, DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart(`isAverageFeeEstimatesCurrentBy${this.props.groupBy}ChartOpen`);
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue),
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.x.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }

    private onChangeRadio(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({
            option: event.target.value as AverageFeeEstimatesPeriodOption
        });
    }

    private onToggleVideo() {
        this.setState({
            isVideoModalOpen: !this.state.isVideoModalOpen
        });
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(AverageFeeEstimatesCurrentChart as any) as any;

export type AverageFeeEstimatesData = {
    statistics: Statistics;
    options:    Options;
};

export type Options = {
    currencySymbol: CurrencySymbol;
};

export type CurrencySymbol = {
    symbol: string;
};

export type Statistics = {
    matter: Matter;
};

export type Matter = {
    averageFeeEstimates: AverageFeeEstimates;
};

export type AverageFeeEstimates = {
    averageFeeEstimateLastSixMonths:    AverageFeeEstimate;
    averageFeeEstimateLastTwelveMonths: AverageFeeEstimate;
    averageFeeEstimateLastTwoYears:     AverageFeeEstimate;
    averageFeeEstimateAlltime:          AverageFeeEstimate;
    feeEstimateDetailsLastSixMonths:    FeeEstimateDetails;
    feeEstimateDetailsLastTwelveMonths: FeeEstimateDetails;
    feeEstimateDetailsLastTwoYears:     FeeEstimateDetails;
    feeEstimateDetailsAll:              FeeEstimateDetails;
};

export type AverageFeeEstimate = {
    averageFeeEstimate: AverageFeeEstimateElement[];
    groupedByCount:     number;
};

export type AverageFeeEstimateElement = {
    amount:    number;
    groupedBy: string;
};

export type FeeEstimateDetails = {
    avgFeeEstimatePerMatter:         number;
    mattersCount:                    number;
    mattersWithNoEstimate:           number;
    mattersWithNoEstimateBilledFees: number;
};

const AverageFeeEstimatesDataQuery = gql`
    query AverageFeesEstimateChartData($groupBy: MatterGroupBy, $entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            matter {
                averageFeeEstimates(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    averageFeeEstimateLastSixMonths {
                        averageFeeEstimate {
                            amount
                            groupedBy
                        }
                        groupedByCount
                    }

                    averageFeeEstimateLastTwelveMonths {
                        averageFeeEstimate {
                            amount
                            groupedBy
                        }
                        groupedByCount
                    }

                    averageFeeEstimateLastTwoYears {
                        averageFeeEstimate {
                            amount
                            groupedBy
                        }
                        groupedByCount
                    }

                    averageFeeEstimateAlltime {
                        averageFeeEstimate {
                            amount
                            groupedBy
                        }
                        groupedByCount
                    }

                    feeEstimateDetailsLastSixMonths {
                        avgFeeEstimatePerMatter
                        mattersCount
                        mattersWithNoEstimate
                        mattersWithNoEstimateBilledFees
                    }

                    feeEstimateDetailsLastTwelveMonths {
                        avgFeeEstimatePerMatter
                        mattersCount
                        mattersWithNoEstimate
                        mattersWithNoEstimateBilledFees
                    }

                    feeEstimateDetailsLastTwoYears {
                        avgFeeEstimatePerMatter
                        mattersCount
                        mattersWithNoEstimate
                        mattersWithNoEstimateBilledFees
                    }

                    feeEstimateDetailsAll {
                        avgFeeEstimatePerMatter
                        mattersCount
                        mattersWithNoEstimate
                        mattersWithNoEstimateBilledFees
                    }
                }
            }
        }
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

// const CurrencySymbol = gql`
//     query CurrencySymbol {
//         options {
//             currencySymbol {
//                 symbol
//             }
//         }
//     }
// `;
